// src/components/FeaturedContentCards.jsx
import { useEffect, useMemo, useRef, useState } from "react";
import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { db } from "../firebase/config";
import PortalIcon from "./ui/PortalIcon";

const DEFAULT_FEATURED = {
  blog: { enabled: true, items: [] },
  materials: { enabled: true, items: [] },
  bundles: { enabled: true, items: [] },
};

const CONTENT_META = {
  blog: { collection: "blogs", page: "blog", label: "Blog", plural: "Blogs", icon: "edit", color: "#10B981" },
  materials: { collection: "materials", page: "materials", label: "Material", plural: "Materials", icon: "book", color: "#F59E0B" },
  bundles: { collection: "bundles", page: "bundles", label: "Bundle", plural: "Bundles", icon: "package", color: "#8B5CF6" },
};

function readDate(value) {
  return value?.toMillis?.() || value?.seconds || 0;
}

function getItemImage(item) {
  return item.thumbnail || item.coverCard || item.coverLandscape || item.image || item.cover || item.coverImage || item.imageUrl || "";
}

function getItemDescription(item) {
  return item.description || item.excerpt || item.summary || item.subtitle || "Explore this hand-picked resource from DG HelpMate.";
}

function mergeSelectedWithLive(selectedItem, liveItems) {
  const liveItem = liveItems.find((item) => item.id === selectedItem.id) || {};
  const merged = { ...selectedItem, ...liveItem };
  return {
    ...merged,
    title: liveItem.title || selectedItem.title || "Untitled",
    description: getItemDescription(merged),
    thumbnail: getItemImage(merged),
    layout: selectedItem.layout || liveItem.layout || "card",
  };
}

function buildFeaturedItems(featuredContent, contentByType) {
  return Object.entries(CONTENT_META).flatMap(([type, meta]) => {
    const config = featuredContent[type] || DEFAULT_FEATURED[type];
    if (!config?.enabled) return [];

    const liveItems = contentByType[type] || [];
    const selected = Array.isArray(config.items) ? config.items.filter((item) => item?.id) : [];
    const itemsToUse = selected.length
      ? selected.map((item) => mergeSelectedWithLive(item, liveItems))
      : liveItems
          .filter((item) => item.status !== "draft" && item.active !== false)
          .sort((a, b) => readDate(b.createdAt || b.updatedAt) - readDate(a.createdAt || a.updatedAt))
          .slice(0, 2)
          .map((item) => ({ ...item, layout: "small-card" }));

    return itemsToUse.map((item) => ({
      ...item,
      type,
      page: meta.page,
      label: meta.label,
      icon: meta.icon,
      accent: meta.color,
    }));
  });
}

function FeaturedCard({ item, t, setPage }) {
  const [hovered, setHovered] = useState(false);
  const image = getItemImage(item);
  const description = getItemDescription(item);
  const price = item.price || item.salePrice || item.amount;
  const layout = item.layout === "button" || item.layout === "small-card" ? item.layout : "card";
  const isButton = layout === "button";
  const isSmall = layout === "small-card";
  const ctaLabel = item.type === "blog"
    ? "Read Blog"
    : item.type === "materials"
      ? "View Material"
      : item.type === "bundles"
        ? "View Bundle"
        : `View ${item.label || "Item"}`;

  const openItem = () => {
    try {
      if (item.type === "blog") {
        if (item.id) localStorage.setItem("selectedBlogId", item.id);
        if (item.title) localStorage.setItem("selectedBlogTitle", item.title);
      } else if (item.type === "materials") {
        if (item.id) localStorage.setItem("selectedMaterialId", item.id);
        if (item.title) localStorage.setItem("selectedMaterialTitle", item.title);
      } else if (item.type === "bundles") {
        if (item.id) localStorage.setItem("selectedBundleId", item.id);
        if (item.title) localStorage.setItem("selectedBundleTitle", item.title);
      }
    } catch (error) {
      console.warn("Unable to save featured item navigation target:", error);
    }
    setPage(item.page);
  };

  return (
    <article
      className={`featured-resource-item featured-layout-${layout}`}
      onClick={openItem}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: t.bgCard,
        border: `1px solid ${hovered ? item.accent : t.border}`,
        borderRadius: isButton ? 999 : 10,
        cursor: "pointer",
        overflow: "hidden",
        display: "flex",
        flexDirection: isSmall ? "row" : "column",
        alignItems: isButton || isSmall ? "center" : "stretch",
        minWidth: 0,
        height: isButton ? "auto" : "100%",
        padding: isButton ? "12px 14px" : 0,
        transition: "transform .22s ease, border-color .22s ease, box-shadow .22s ease",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
        boxShadow: hovered ? `0 18px 38px ${item.accent}22` : isButton ? "none" : `0 12px 28px ${t.shadow || "rgba(0,0,0,.08)"}`,
      }}
    >
      {!isButton && (
        <div
          className="featured-media"
          style={{
            background: image ? t.bgCard2 || t.bg : `linear-gradient(135deg, ${item.accent}24, ${t.bgCard2 || t.bg})`,
            borderRight: isSmall ? `1px solid ${t.border}` : "none",
          }}
        >
          {image ? (
            <img
              src={image}
              alt={item.title || item.label}
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                transform: hovered ? "scale(1.04)" : "scale(1)",
                transition: "transform .35s ease",
              }}
            />
          ) : (
            <div style={{ height: "100%", display: "grid", placeItems: "center", color:item.accent }}>
              <PortalIcon name={item.icon} size={isSmall ? 28 : 42} />
            </div>
          )}
          {!isSmall && (
            <div
              style={{
                position: "absolute",
                top: 12,
                left: 12,
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
                background: t.bg,
                border: `1px solid ${t.border}`,
                color: item.accent,
                borderRadius: 999,
                padding: "6px 10px",
                fontSize: ".74rem",
                fontWeight: 800,
                boxShadow: "0 8px 18px rgba(0,0,0,.16)",
              }}
            >
              <PortalIcon name={item.icon} size={14} />
              {item.label}
            </div>
          )}
        </div>
      )}

      <div className="featured-body">
        {isButton && (
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              background: `${item.accent}18`,
              color: item.accent,
              flex: "0 0 auto",
            }}
          >
            <PortalIcon name={item.icon} size={17} />
          </div>
        )}

        <div style={{ minWidth: 0, flex: 1 }}>
          {isSmall && (
            <div style={{ color: item.accent, fontSize: ".72rem", fontWeight: 900, marginBottom: 3 }}>
              <PortalIcon name={item.icon} size={13} style={{ marginRight:5 }} />{item.label}
            </div>
          )}
          <h3 className="featured-title" style={{ color: t.text }}>
            {item.title || "Untitled"}
          </h3>
        </div>

        {!isButton && (
          <p className="featured-desc" style={{ color: t.muted }}>
            {description}
          </p>
        )}

        {!isButton && (
          <div className="featured-foot">
            <div style={{ color: price ? t.gold : t.muted, fontSize: ".8rem", fontWeight: 800 }}>
              {price ? `₹${Number(price).toLocaleString("en-IN")}` : "Featured"}
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                openItem();
              }}
              style={{
                border: `1px solid ${item.accent}44`,
                background: hovered ? item.accent : `${item.accent}14`,
                color: hovered ? "#fff" : item.accent,
                borderRadius: 8,
                padding: isSmall ? "7px 10px" : "9px 12px",
                fontSize: ".8rem",
                fontWeight: 800,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all .2s ease",
                whiteSpace: "nowrap",
              }}
            >
              {ctaLabel}
            </button>
          </div>
        )}

        {isButton && <span className="featured-button-open" style={{ color: item.accent }}>{ctaLabel}</span>}
      </div>
    </article>
  );
}

export default function FeaturedContentCards({ t, setPage }) {
  const [featuredContent, setFeaturedContent] = useState(DEFAULT_FEATURED);
  const [contentByType, setContentByType] = useState({ blog: [], materials: [], bundles: [] });
  const [loading, setLoading] = useState(true);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    let mounted = true;

    async function loadData() {
      try {
        const siteContentSnap = await getDoc(doc(db, "siteContent", "main"));
        const siteContentData = siteContentSnap.exists() ? siteContentSnap.data() : {};

        const snapshots = await Promise.all(
          Object.entries(CONTENT_META).map(async ([type, meta]) => {
            const snap = await getDocs(collection(db, meta.collection));
            return [type, snap.docs.map((itemDoc) => ({ id: itemDoc.id, ...itemDoc.data() }))];
          })
        );

        if (!mounted) return;
        setFeaturedContent({ ...DEFAULT_FEATURED, ...(siteContentData.featuredContent || {}) });
        setContentByType(Object.fromEntries(snapshots));
      } catch (error) {
        console.error("Error loading featured content cards:", error);
      } finally {
        if (mounted) setLoading(false);
      }
    }

    loadData();
    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.18, rootMargin: "0px 0px -80px 0px" }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const featuredItems = useMemo(
    () => buildFeaturedItems(featuredContent, contentByType).slice(0, 9),
    [featuredContent, contentByType]
  );

  const enabledTypes = Object.entries(CONTENT_META)
    .filter(([type]) => featuredContent[type]?.enabled !== false)
    .map(([, meta]) => meta.plural);
  const buttonItems = featuredItems.filter((item) => item.layout === "button");
  const smallItems = featuredItems.filter((item) => item.layout === "small-card");
  const bigItems = featuredItems.filter((item) => item.layout !== "button" && item.layout !== "small-card");

  if (loading) {
    return (
      <section style={{ padding: "clamp(56px,8vw,84px) clamp(16px,5%,60px)", background: t.bg }}>
        <style>{`
          @keyframes featured-skeleton-pulse {
            0%, 100% { opacity: .55; }
            50% { opacity: 1; }
          }
          .featured-skeleton-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(min(100%, 280px), 1fr));
            gap: 18px;
          }
          .featured-skeleton-card {
            min-height: 260px;
            border-radius: 10px;
            animation: featured-skeleton-pulse 1.35s ease-in-out infinite;
          }
        `}</style>
        <div style={{ maxWidth: 1180, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 24 }}>
            <div style={{ width: 150, height: 14, borderRadius: 999, background: t.bgCard2, margin: "0 auto 14px" }} />
            <div style={{ width: "min(100%, 420px)", height: 28, borderRadius: 8, background: t.bgCard2, margin: "0 auto" }} />
          </div>
          <div className="featured-skeleton-grid">
            {[0, 1, 2].map((item) => (
              <div key={item} className="featured-skeleton-card" style={{ background: t.bgCard, border: `1px solid ${t.border}`, padding: 16 }}>
                <div style={{ height: 132, borderRadius: 8, background: t.bgCard2, marginBottom: 16 }} />
                <div style={{ height: 14, width: "42%", borderRadius: 999, background: t.bgCard2, marginBottom: 12 }} />
                <div style={{ height: 20, width: "82%", borderRadius: 7, background: t.bgCard2, marginBottom: 10 }} />
                <div style={{ height: 12, width: "100%", borderRadius: 999, background: t.bgCard2, marginBottom: 8 }} />
                <div style={{ height: 12, width: "68%", borderRadius: 999, background: t.bgCard2 }} />
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  if (featuredItems.length === 0) return null;

  return (
    <section
      ref={sectionRef}
      className={inView ? "featured-resources-in" : "featured-resources-out"}
      style={{
        padding: "clamp(56px,8vw,88px) clamp(16px,5%,60px)",
        background: t.bg,
        borderTop: `1px solid ${t.border}`,
        borderBottom: `1px solid ${t.border}`,
        position: "relative",
        overflow: "hidden",
      }}
    >
      <style>{`
        .featured-content-wrap { max-width: 1180px; margin: 0 auto; position: relative; }
        .featured-content-head { text-align: center; max-width: 720px; margin: 0 auto 24px; }
        .featured-content-actions { display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; margin-bottom: 28px; }
        .featured-button-row { display: flex; flex-wrap: wrap; gap: 10px; justify-content: center; margin-bottom: 18px; }
        .featured-small-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 460px), 460px)); gap: 16px; margin-bottom: 18px; align-items: stretch; justify-content: center; }
        .featured-big-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 520px), 520px)); gap: 18px; align-items: stretch; justify-content: center; }
        .featured-resources-in .featured-content-head,
        .featured-resources-in .featured-content-actions,
        .featured-resources-in .featured-resource-item {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
        .featured-resources-in .featured-resource-item {
          animation: featured-card-rise .62s cubic-bezier(.2,.8,.2,1) both;
        }
        @keyframes featured-card-rise {
          0% { opacity: 0; transform: translateY(34px) scale(.94); filter: blur(5px); }
          58% { opacity: 1; transform: translateY(-6px) scale(1.025); filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        .featured-content-head,
        .featured-content-actions {
          transition: opacity .55s ease, transform .55s cubic-bezier(.2,.8,.2,1);
        }
        .featured-resource-item {
          transition: opacity .55s ease, transform .55s cubic-bezier(.2,.8,.2,1), border-color .22s ease, box-shadow .22s ease;
        }
        .featured-resources-in .featured-resource-item:nth-of-type(1) { transition-delay: .04s; }
        .featured-resources-in .featured-resource-item:nth-of-type(2) { transition-delay: .10s; }
        .featured-resources-in .featured-resource-item:nth-of-type(3) { transition-delay: .16s; }
        .featured-resources-in .featured-resource-item:nth-of-type(4) { transition-delay: .22s; }
        .featured-resources-in .featured-resource-item:nth-of-type(5) { transition-delay: .28s; }
        .featured-resources-in .featured-resource-item:nth-of-type(6) { transition-delay: .34s; }
        .featured-layout-button { width: auto; min-width: 220px; max-width: min(100%, 360px); }
        .featured-media { position: relative; overflow: hidden; height: 178px; width: 100%; flex: 0 0 auto; }
        .featured-layout-small-card .featured-media { width: 96px; height: 96px; }
        .featured-body { padding: 18px; display: flex; flex-direction: column; flex: 1; min-width: 0; min-height: 210px; }
        .featured-layout-small-card .featured-body { padding: 12px 14px; min-height: 0; }
        .featured-layout-button .featured-body { padding: 0; flex-direction: row; align-items: center; gap: 10px; min-height: 0; }
        .featured-title, .featured-desc { display: -webkit-box; -webkit-box-orient: vertical; overflow: hidden; margin: 0; }
        .featured-title { -webkit-line-clamp: 2; font-size: 1.08rem; line-height: 1.34; font-weight: 850; }
        .featured-layout-small-card .featured-title, .featured-layout-button .featured-title { font-size: .92rem; }
        .featured-layout-button .featured-title { margin: 0; }
        .featured-desc { -webkit-line-clamp: 3; font-size: .86rem; line-height: 1.6; flex: 1; margin-top: 8px; }
        .featured-layout-small-card .featured-desc { -webkit-line-clamp: 2; margin-top: 6px; }
        .featured-foot { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-top: 18px; }
        .featured-layout-small-card .featured-foot { margin-top: 10px; }
        .featured-button-open { font-size: .82rem; font-weight: 900; flex: 0 0 auto; }
        @media (max-width: 980px) {
          .featured-small-grid, .featured-big-grid { grid-template-columns: minmax(0, 620px); }
          .featured-layout-card, .featured-layout-small-card { max-width: 620px; width: 100%; margin: 0 auto; }
        }
        @media (max-width: 640px) {
          .featured-button-row { justify-content: stretch; }
          .featured-layout-button { width: 100%; max-width: none; }
          .featured-layout-card { max-width: 440px; margin: 0 auto; width: 100%; }
          .featured-layout-small-card { flex-direction: row !important; }
        }
      `}</style>

      <div className="featured-content-wrap">
        <div className="featured-content-head">
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              background: `${t.gold}14`,
              border: `1px solid ${t.gold}35`,
              color: t.gold,
              fontSize: ".76rem",
              fontWeight: 800,
              textTransform: "uppercase",
              padding: "6px 13px",
              borderRadius: 999,
              marginBottom: 14,
            }}
          >
            <PortalIcon name="star" size={14} />
            Featured Resources
          </div>
          <h2 style={{ color: t.text, fontSize: "clamp(1.65rem,3.5vw,2.45rem)", lineHeight: 1.18, margin: "0 0 10px", fontWeight: 900 }}>
            Fresh picks for teachers and coaching owners
          </h2>
          <p style={{ color: t.muted, fontSize: ".96rem", lineHeight: 1.7, margin: 0 }}>
            {enabledTypes.join(", ")} selected from your dashboard for quick discovery.
          </p>
        </div>

        <div className="featured-content-actions">
          {Object.entries(CONTENT_META).map(([type, meta]) => (
            <button
              key={type}
              onClick={() => setPage(meta.page)}
              style={{
                border: `1px solid ${t.border}`,
                background: t.bgCard,
                color: t.text,
                borderRadius: 10,
                padding: "10px 13px",
                fontSize: ".82rem",
                fontWeight: 800,
                cursor: "pointer",
                fontFamily: "inherit",
              }}
            >
              <PortalIcon name={meta.icon} size={15} style={{ marginRight: 6, verticalAlign: "-2px" }} />
              {meta.plural}
            </button>
          ))}
        </div>

        {buttonItems.length > 0 && (
          <div className="featured-button-row">
            {buttonItems.map((item) => (
              <FeaturedCard key={`${item.type}-${item.id}`} item={item} t={t} setPage={setPage} />
            ))}
          </div>
        )}

        {smallItems.length > 0 && (
          <div className="featured-small-grid">
            {smallItems.map((item) => (
              <FeaturedCard key={`${item.type}-${item.id}`} item={item} t={t} setPage={setPage} />
            ))}
          </div>
        )}

        {bigItems.length > 0 && (
          <div className="featured-big-grid">
            {bigItems.map((item) => (
              <FeaturedCard key={`${item.type}-${item.id}`} item={item} t={t} setPage={setPage} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
